import { useParams } from "react-router-dom";
import { Button } from "reactstrap";
import pluralize from "pluralize";
import { useState } from "react";

import SelectablePopover from "components/Records/References/SelectablePopover";
import SelectableInput from "components/SelectableInput/SelectableInput";
import useAllTags from "hooks/useAllTags";
import useModule from "hooks/useModule";
import usePage from "api/api-hooks/pages/usePage";
import AppDrawer from "../AppDrawer";
import useBulkUpdateRecords from "api/api-hooks/records/useBulkUpdateRecords";
import { newToastEvent } from "utils/ToastUtil";
import AppSpinner from "../../../elements/AppSpinner";
import StrUtils from "../../../utils/StrUtils";

const BulkApplyTagsDrawer = ({
  open,
  onClose,
  records,
  moduleAPIName,
  parentRecordID = null, // Providing this means we are updating subitems.
  multiSubitemAPIName = null, // Providing this means we are updating subitems.
  view,
}) => {
  const { pageID, workspaceID } = useParams();
  const { page } = usePage(pageID);
  const { module } = useModule(moduleAPIName);
  const { tags, tagsLoading } = useAllTags(moduleAPIName);
  const [selectedTags, setSelectedTags] = useState([]);
  const { bulkUpdate, startingUpdate } = useBulkUpdateRecords();

  const handleClose = () => {
    setSelectedTags([]);
    onClose();
  };

  // Only used when parentRecordID and multiSubitemAPIName is provided
  const updateSubitemTags = (apply) => {
    const tagIds = selectedTags.map((t) => t._id || t.id);
    const payload = {
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activity: {
          id: StrUtils.uuidv4(),
          Type: "bulk-update-in-page",
          Page: {
            id: page.id,
            Name: page.Name,
            Logo: page.Logo,
          },
          ...(view && {
            View: {
              View: {
                id: view.id,
                Name: view.Name,
                Type: view.Type,
              },
            },
          }),
        },
      },
      Ids: [
        {
          APIName: multiSubitemAPIName,
          Ids: records,
          Value: [{ APIName: "Tags", Value: tagIds, Apply: apply }],
        },
      ],
    };

    bulkUpdate({
      moduleAPIName: moduleAPIName,
      recordIds: [parentRecordID],
      payload: payload,
      onSuccess: () => {
        onClose();
      },
    });
    setSelectedTags([]);
  };

  const updateRecordsTags = (apply) => {
    if (parentRecordID && multiSubitemAPIName) {
      return updateSubitemTags(apply);
    }

    const recordIds = records.map((r) => r?.id || r?._id);
    const tagIds = selectedTags.map((t) => t.id);
    const payload = {
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activitiy: {
          id: StrUtils.uuidv4(),
          Type: "bulk-update-in-page",
          Page: {
            id: page.id,
            Name: page.Name,
            Logo: page.Logo,
          },
          View: {
            id: view.id,
            Name: view.Name,
            Type: view.Type,
          },
        },
      },
      Ids: [{ APIName: "Tags", Value: tagIds, Apply: apply }],
    };

    bulkUpdate({
      moduleAPIName: moduleAPIName,
      recordIds: recordIds,
      payload: payload,
      onSuccess: () => {
        onClose();
        newToastEvent("success", "Successfully started tag update.");
      },
    });
    setSelectedTags([]);
  };

  const handleSelect = (tagToAdd) => {
    setSelectedTags((prev) => [...prev, tagToAdd]);
  };

  const handleDeselect = (tagToRemove) => {
    setSelectedTags((prev) => prev.filter((t) => t.id !== tagToRemove.id));
  };

  return (
    <AppDrawer
      open={open}
      onClose={handleClose}
      headerTitle={
        "Updating tags for " +
        pluralize(module?.Name ? module.Name : "Record", records?.length, true)
      }
      headerOptions={
        <>
          <Button
            style={{ width: 105 }}
            size="sm"
            color="danger"
            onClick={() => updateRecordsTags(false)}
            disabled={startingUpdate || !selectedTags.length}
          >
            {startingUpdate ? <AppSpinner size="sm" /> : "Remove Tags"}
          </Button>
          <Button
            style={{ width: 90 }}
            size="sm"
            color="primary"
            onClick={() => updateRecordsTags(true)}
            disabled={startingUpdate || !selectedTags.length}
          >
            {startingUpdate ? (
              <AppSpinner size="sm" color="white" />
            ) : (
              "Add Tags"
            )}
          </Button>
        </>
      }
      bodyCSS="pt-2"
      body={
        <>
          <h5>Select Tags</h5>
          <SelectableInput
            options={tags}
            value={selectedTags}
            loadingOptions={tagsLoading}
            labelKey="Name"
            valueKey="id"
            colorKey="Color"
            onSelect={handleSelect}
            onDeselect={handleDeselect}
            onClear={() => setSelectedTags([])}
            multiselect={true}
            groupBy={["Scope", "Type"]}
            nestedGroupBy="TagGroup"
            groupByLabels={{
              Person: "Your Tags",
              Cloud: "Cloud Tags",
              Module: "Module Tags",
              Workspace: "Workspace Tags",
            }}
          />
          {selectedTags?.length > 0 && (
            <div className="border rounded mt-4 w-100">
              <h3 className="pt-3 text-center mb-0">Selected Tags</h3>
              <SelectablePopover
                options={selectedTags}
                groupBy={["Scope", "Type"]}
                sortBy={"Name"}
                full
              />
            </div>
          )}
        </>
      }
    />
  );
};

export default BulkApplyTagsDrawer;
